import React from 'react';
import { Card, Row, Col, Typography, Space, Button, Divider, Empty, Tag } from 'antd';
import {
    FileTextOutlined,
    BarChartOutlined,
    TeamOutlined,
    CalendarOutlined,
    BarsOutlined,
    CheckCircleOutlined,
    PrinterOutlined,
    MessageOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import useResponsive from '../hooks/useResponsive';
import './ReportsPage.css';

const { Title, Paragraph, Text } = Typography;

const ReportsPage = () => {
    const { isMobile } = useResponsive();

    // Define all available reports
    const reports = [
        // {
        //     id: 'student-marks',
        //     title: 'Student Mark Sheet',
        //     description: 'View individual student mark sheets and performance details',
        //     icon: <FileTextOutlined />,
        //     link: '/reports/student-marks',
        //     badge: 'View by Student',
        //     color: '#1890ff',
        // },
        {
            id: 'class-marks',
            title: 'Class-wise Marks',
            description: 'View marks report for an entire class',
            icon: <BarChartOutlined />,
            link: '/reports/class-marks',
            badge: 'Aggregate',
            color: '#2563EB',
        },
        {
            id: 'exam-marks',
            title: 'Exam-wise Marks',
            description: 'View marks organized by exam type',
            icon: <FileTextOutlined />,
            link: '/reports/exam-marks',
            badge: 'Evaluation',
            color: '#7C3AED',
        },
        {
            id: 'marks-range',
            title: 'Performance Range',
            description: 'Filter students within specific mark percentage ranges',
            icon: <CalendarOutlined />,
            link: '/reports/marks-range',
            badge: 'Analysis',
            color: '#DB2777',
        },
        {
            id: 'class-attendance',
            title: 'Class Attendance',
            description: 'Daily and monthly attendance reports for classes',
            icon: <CheckCircleOutlined />,
            link: '/reports/class-attendance',
            badge: 'Attendance',
            color: '#059669',
        },
        {
            id: 'gender-report',
            title: 'Gender Demographics',
            description: 'Statistical breakdown of students by gender',
            icon: <BarsOutlined />,
            link: '/reports/gender',
            badge: 'Stats',
            color: '#4F46E5',
        },
        {
            id: 'bpl-report',
            title: 'BPL Census',
            description: 'List and count of Below Poverty Line (BPL) students',
            icon: <TeamOutlined />,
            link: '/reports/bpl-students',
            badge: 'Welfare',
            color: '#0891B2',
        },
        {
            id: 'scst-report',
            title: 'SC/ST Census',
            description: 'List of Scheduled Caste/Tribe students for compliance',
            icon: <TeamOutlined />,
            link: '/reports/scst-students',
            badge: 'Compliance',
            color: '#0D9488',
        },
        {
            id: 'student-printing',
            title: 'Student Document Center',
            description: 'Generate ID cards and detailed student profile documents',
            icon: <PrinterOutlined />,
            link: '/reports/student-printing',
            badge: 'Export',
            color: '#EA580C',
        },
        {
            id: 'tutor-feedback',
            title: 'Tutor Feedback Monitor',
            description: 'Monitor feedback submission status and track student follow-up by tutors',
            icon: <MessageOutlined />,
            link: '/feedback-dashboard',
            badge: 'Tutor Feedback',
            color: '#10B981',
        },
    ];

    // Group reports by category
    const reportCategories = [
        {
            name: 'Administrative & Exports',
            icon: <PrinterOutlined />,
            reports: reports.filter(r => r.id.includes('printing') || r.id.includes('report') || r.id.includes('census') || r.id.includes('feedback')),
        },
        {
            name: 'Academic Performance',
            icon: <FileTextOutlined />,
            reports: reports.filter(r => r.id.includes('mark') || r.id.includes('range')),
        },
        {
            name: 'Operations & Attendance',
            icon: <CheckCircleOutlined />,
            reports: reports.filter(r => r.id.includes('attendance')),
        },
    ];

    const ReportCard = ({ report }) => (
        <Card
            hoverable
            className="report-card"
            style={{
                height: '100%',
                borderRadius: '12px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
            bodyStyle={{ padding: '24px' }}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ 
                        width: 48, 
                        height: 48, 
                        borderRadius: '10px', 
                        backgroundColor: `${report.color}15`, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: 24,
                        color: report.color
                    }}>
                        {report.icon}
                    </div>
                    <Tag color={report.color} style={{ borderRadius: '6px', border: 'none', fontWeight: 600 }}>
                        {report.badge.toUpperCase()}
                    </Tag>
                </div>
                <div>
                    <Title level={5} style={{ margin: 0, fontWeight: 700 }}>
                        {report.title}
                    </Title>
                    <Paragraph type="secondary" style={{ fontSize: 13, marginTop: 8, height: 40, overflow: 'hidden' }}>
                        {report.description}
                    </Paragraph>
                </div>
                <Link to={report.link} style={{ textDecoration: 'none' }}>
                    <Button type="primary" block style={{ backgroundColor: report.color, border: 'none', borderRadius: '8px' }}>
                        Generate Report
                    </Button>
                </Link>
            </Space>
        </Card>
    );

    return (
        <div style={{ padding: isMobile ? '16px' : '32px', backgroundColor: '#F8FAFC' }}>
            <Space direction="vertical" style={{ width: '100%' }} size={isMobile ? 'large' : 'xl'}>
                {/* Page Header */}
                <div>
                    <Title level={2} style={{ fontWeight: 800, margin: 0 }}>Reports Center</Title>
                    <Paragraph type="secondary" style={{ fontSize: 16 }}>
                        Generate, view, and export detailed academic and administrative analytics.
                    </Paragraph>
                </div>

                {/* Reports by Category */}
                {reportCategories.map((category) => (
                    <div key={category.name} style={{ marginBottom: 40 }}>
                        <Divider orientation="left" style={{ borderTopColor: '#E2E8F0' }}>
                            <Space>
                                <span style={{ fontSize: 18, color: '#64748B' }}>{category.icon}</span>
                                <Text strong style={{ fontSize: 16, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {category.name}
                                </Text>
                            </Space>
                        </Divider>
                        <Row gutter={[24, 24]}>
                            {category.reports.map((report) => (
                                <Col xs={24} sm={12} lg={8} key={report.id}>
                                    <ReportCard report={report} />
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}
            </Space>
        </div>
    );
};

export default ReportsPage;
